export interface KkiRateLike {
	kkValue: number;
}

export interface AnchorPairInput {
	recordedKk: number | null | undefined;
	actualKk: number | null | undefined;
	precision?: number;
}

export interface AnchorPairResult {
	showActual: boolean;
	driftPercent: number | null;
	recordedRounded: number | null;
	actualRounded: number | null;
}

export function shouldShowAnchor(input: {
	anchorType: string | null | undefined;
	anchorKkOrigin: number | null | undefined;
}): boolean {
	return input.anchorType === "kki";
}

export function hasCompleteAnchor(input: {
	anchorType: string | null | undefined;
	anchorKkOrigin: number | null | undefined;
}): boolean {
	return (
		input.anchorType === "kki" &&
		input.anchorKkOrigin != null &&
		input.anchorKkOrigin > 0
	);
}

/** KK units = nominal_amount / kk_value (local-currency cost of 1 KK). */
export function computeKkValue(
	amount: number,
	rate: KkiRateLike | null | undefined,
): number | null {
	if (!rate || rate.kkValue <= 0 || amount <= 0) return null;
	return amount / rate.kkValue;
}

export function computeKkFromRate(
	amount: number | null | undefined,
	kkRate: number | null | undefined,
): number | null {
	if (amount == null || kkRate == null || kkRate <= 0) return null;
	const n = typeof amount === "number" ? amount : Number.parseFloat(String(amount));
	if (!Number.isFinite(n) || n <= 0) return null;
	return n / kkRate;
}

export function computeDriftPercent(
	originKk: number,
	actualKk: number,
): number | null {
	if (originKk <= 0) return null;
	return Math.round(((actualKk - originKk) / originKk) * 1000) / 10;
}

function roundKk(value: number, precision: number): number {
	const factor = 10 ** precision;
	return Math.round(value * factor) / factor;
}

export function anchorValuesDiffer(
	a: number,
	b: number,
	precision = 1,
): boolean {
	return roundKk(a, precision) !== roundKk(b, precision);
}

export function buildAnchorPair(input: AnchorPairInput): AnchorPairResult {
	const precision = input.precision ?? 1;
	const recorded =
		input.recordedKk != null && input.recordedKk > 0
			? input.recordedKk
			: null;
	const actual =
		input.actualKk != null && input.actualKk > 0 ? input.actualKk : null;

	if (recorded == null) {
		return {
			showActual: actual != null,
			driftPercent: null,
			recordedRounded: null,
			actualRounded: actual != null ? roundKk(actual, precision) : null,
		};
	}

	const recordedRounded = roundKk(recorded, precision);
	const actualRounded =
		actual != null ? roundKk(actual, precision) : null;
	const showActual =
		actualRounded != null &&
		actual != null &&
		anchorValuesDiffer(recorded, actual, precision);
	const driftPercent =
		showActual && actual != null
			? computeDriftPercent(recorded, actual)
			: null;

	return { showActual, driftPercent, recordedRounded, actualRounded };
}

export function formatKkLabel(kk: number, precision = 1): string {
	return `≈ ${kk.toFixed(precision)} KK`;
}

export function formatAnchorDetailLine(input: {
	kk: number | string;
	kkiVersion: string | null;
	country: string | null;
	precision?: number;
}): string {
	const kkNum = Number.parseFloat(String(input.kk));
	const prec = input.precision ?? 2;
	const kkLabel = Number.isFinite(kkNum) ? kkNum.toFixed(prec) : String(input.kk);
	const parts = [`≈ ${kkLabel} KK`];
	if (input.kkiVersion) parts.push(input.kkiVersion);
	if (input.country) parts.push(input.country);
	return parts.join(" · ");
}

export function toKkiMonthParam(isoDate: string): string {
	const d = new Date(isoDate);
	const y = d.getUTCFullYear();
	const m = String(d.getUTCMonth() + 1).padStart(2, "0");
	return `${y}-${m}-01`;
}
